import api from "./api";
import type { ISharePointFile,ISnapshot } from "./sharepointService";
import { backgroundTaskManager, type TaskProgress } from "./backgroundTaskService";
import { globalModalManager } from "./globalModalService";

/**
 * Generate unique session ID for file processing isolation
 * Format: timestamp_randomString to ensure uniqueness across concurrent users
 */
const generateSessionId = (): string => {
  return `${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
};

export interface INotification {
    id: string;
    file_name: string;
    old_cpr_date: string;
    new_cpr_date: string;
  }
  
export interface IBusinessCase {
  materials: IMaterialSubmission[];
  latest_version: any;
  id: string;
  project_name: string;
  bc_owner: string; // bc_owner from latest version
  gpm: string;
  bu_name: string; // business unit name
  cpr_date: string | null; // from latest version
  created_at: string;
  created_by_name: string;
  last_updated_at: string | null; // from latest version
  last_updated_by_name: string | null; // from latest version
  latest_version_number: string | null;
  notifications?: INotification[];
  snapshots?:ISnapshot[];
  is_deleted?: boolean;
  deleted_at?: string | null;
  deleted_by_name?: string | null;
}

export interface IBusinessUnit {
    id: number;
    name: string;
}

export interface IMaterialSubmission {
    id?: string;
    materialType?: 'new' | 'substitution';
    productFamily: string;
    productGroup: string;
    productSubGroup: string;
    material: string;
}

export interface ICreateBusinessCaseData {
    businessCaseName: string;
    sharepointFile: ISharePointFile;
    productGpm: string;
    businessUnit: IBusinessUnit;
    materialSubmissions: IMaterialSubmission[];
}

export const getBusinessUnits = async (): Promise<IBusinessUnit[]> => {
    try {
        const response = await api.get('/business-cases/business-units/');
        if (response.data && response.data.results) {
            return response.data.results;
        }
        return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
        console.error('Error fetching business units:', error);
        throw error;
    }
};

export const getBusinessCases = async (params?: {
    search?: string;
    sortField?: string;   
    sortOrder?: "asc" | "desc";
    filters?: Record<string, string>;
    page?: number;
    pageSize?: number;
  }): Promise<{ data: IBusinessCase[]; total: number }> => {
    try {
      // Transform camelCase params to snake_case for backend
      const apiParams: any = {};
      if (params?.search) apiParams.search = params.search;
      if (params?.sortField) apiParams.sortField = params.sortField;
      if (params?.sortOrder) apiParams.sortOrder = params.sortOrder;
      if (params?.page) apiParams.page = params.page;
      if (params?.pageSize) apiParams.page_size = params.pageSize; // Transform pageSize to page_size
      if (params?.filters) {
        Object.assign(apiParams, params.filters);
      }

      const response = await api.get("/business-cases/", { params: apiParams });
  
      return {
        data: response.data.results || [],
        total: response.data.count || (response.data.results?.length ?? 0),
      };
    } catch (error) {
      console.error("Error fetching business cases:", error);
      throw error;
    }
  };

export const getBusinessCaseById = async (id: string): Promise<IBusinessCase> => {
    try {
        const response = await api.get(`/business-cases/${id}/`);
        return response.data;
    } catch (error) {
        console.error(`Error fetching business case ${id}:`, error);
        throw error;
    }
};

export const createBusinessCase = async (data: ICreateBusinessCaseData): Promise<IBusinessCase> => {
    try {
        const sessionId = generateSessionId();
        const response = await api.post('/business-cases/create/', { ...data, sessionId }, { timeout: 120000 });
        return response.data;
    } catch (error) {
        console.error('Error creating business case:', error);
        throw error;
    }
};

/**
 * Create business case in background with progress tracking
 * Integrates with global modal manager for cross-page modal handling
 */
export const createBusinessCaseInBackground = async (
    data: ICreateBusinessCaseData,
    onProgress?: (progress: TaskProgress) => void,
    deletedProjectAlreadyChecked?: IDeletedProjectCheckResponse
): Promise<{ taskId: string; result: IBusinessCase }> => {
    const taskId = `create_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const steps = [];
    
    // Only check for deleted projects if not already checked
    if (!deletedProjectAlreadyChecked) {
        steps.push({
            name: 'Checking for deleted projects',
            fn: async () => {
                const result = await checkDeletedProject(data.sharepointFile.name);
                
                if (result.has_deleted_project) {
                    // Pause task and show global modal
                    backgroundTaskManager.pauseTask(taskId);
                    
                    return new Promise<IDeletedProjectCheckResponse>((resolve, reject) => {
                        globalModalManager.showDeletedProjectWarning({
                            projectName: result.project_name || 'Unknown',
                            deletedAt: result.deleted_at || '',
                            deletedByName: result.deleted_by_name || 'Unknown',
                            bcId: result.bc_id || 0,
                            fileName: data.sharepointFile.name,
                            taskId,
                            onConfirm: async () => {
                                try {
                                    // Delete the old business case
                                    if (result.bc_id) {
                                        await permanentlyDeleteBusinessCase(result.bc_id.toString());
                                    }
                                    // Resume task
                                    backgroundTaskManager.resumeTask(taskId);
                                    resolve(result);
                                } catch (error: any) {
                                    reject(error);
                                }
                            },
                            onCancel: () => {
                                backgroundTaskManager.cancelTask(taskId);
                                reject(new Error('User cancelled operation'));
                            },
                        });
                    });
                }
                
                return result;
            },
            timeout: 120000
        });
    }
    
    steps.push(
        {
            name: 'Validating file',
            fn: async () => {
                const validation = await validateFile({
                    fileName: data.sharepointFile.name,
                    fileId: data.sharepointFile.id,
                    validationType: 'create'
                });
                
                if (!validation.is_valid) {
                    throw new Error(validation.error_message || 'File validation failed');
                }
                
                // Check if project already exists (bc_id returned with error_type 'existing_project')
                if (validation.error_type === 'existing_project' && validation.bc_id) {
                    // Pause task and show global modal
                    backgroundTaskManager.pauseTask(taskId);
                    
                    return new Promise((_resolve, reject) => {
                        globalModalManager.showExistingProjectWarning({
                            projectName: validation.project_name || 'Unknown',
                            bcName: validation.bc_name || '',
                            gpm: validation.gpm || '',
                            bcId: validation.bc_id!,
                            taskId,
                            onCancel: () => {
                                backgroundTaskManager.cancelTask(taskId);
                                reject(new Error('User cancelled - project already exists'));
                            },
                        });
                    });
                }
                
                return validation;
            },
            timeout: 120000
        },
        {
            name: 'Creating business case',
            fn: async () => {
                return await createBusinessCase(data);
            },
            timeout: 120000
        }
    );

    if (onProgress) {
        backgroundTaskManager.subscribe(taskId, onProgress);
    }

    const result = await backgroundTaskManager.executeTask(
        'create',
        data.businessCaseName,
        steps,
        taskId
    );

    return { taskId, result: result.result };
};

export const updateBusinessCase = async (
  businessCaseId: string,
  selectedFile: ISharePointFile | null,
  rows: { new: IMaterialSubmission[]; old: IMaterialSubmission[] },
  projectNameChoice?: 'original' | 'new'
): Promise<any> => {
  try {
    const sessionId = generateSessionId();
    // Prepare payload
    const payload: any = {
      sessionId,
      materialSubmissions: [
        ...rows.new.map((mat) => ({
          productFamily: mat.productFamily,
          productGroup: mat.productGroup,
          productSubGroup: mat.productSubGroup,
          material: mat.material,
          materialType: "new",
        })),
        ...rows.old.map((mat) => ({
          productFamily: mat.productFamily,
          productGroup: mat.productGroup,
          productSubGroup: mat.productSubGroup,
          material: mat.material,
          materialType: "substitution",
        })),
      ],
    };

    // Include file if provided (backend will check CPR date)
    if (selectedFile) {
      payload.sharepointFile = {
        id: selectedFile.id,
        name: selectedFile.name,
        webUrl: selectedFile.webUrl,
        lastModified: selectedFile.lastModified,
        size: selectedFile.size,
        contentType: selectedFile.contentType,
      };
    }

    // Include project name choice if provided
    if (projectNameChoice) {
      payload.projectNameChoice = projectNameChoice;
    }

    const response = await api.put(`/business-cases/${businessCaseId}/update/`, payload, { timeout: 120000 });
    return response.data;
    
  } catch (error: any) {
    console.error("Error updating business case:", error);
    throw error;
  }
};

/**
 * Update business case in background with progress tracking
 */
export const updateBusinessCaseInBackground = async (
    businessCaseId: string,
    businessCaseName: string,
    selectedFile: ISharePointFile | null,
    rows: { new: IMaterialSubmission[]; old: IMaterialSubmission[] },
    projectNameChoice?: 'original' | 'new',
    onProgress?: (progress: TaskProgress) => void,
    deletedProjectAlreadyChecked?: IDeletedProjectCheckResponse
): Promise<{ taskId: string; result: any }> => {
    const taskId = `update_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const steps = [];
    
    // Track the user's choice from modal (will be set by resume callback)
    let userProjectNameChoice = projectNameChoice;

    // Only check deleted and validate if a file is being updated
    if (selectedFile) {
        // Only check for deleted projects if not already checked
        if (!deletedProjectAlreadyChecked) {
            steps.push({
                name: 'Checking for deleted projects',
                fn: async () => {
                    const result = await checkDeletedProject(selectedFile.name);
                    
                    if (result.has_deleted_project) {
                        // Pause task and show global modal
                        backgroundTaskManager.pauseTask(taskId);
                        
                        return new Promise<IDeletedProjectCheckResponse>((resolve, reject) => {
                            globalModalManager.showDeletedProjectWarning({
                                projectName: result.project_name || 'Unknown',
                                deletedAt: result.deleted_at || '',
                                deletedByName: result.deleted_by_name || 'Unknown',
                                bcId: result.bc_id || 0,
                                fileName: selectedFile.name,
                                taskId,
                                onConfirm: async () => {
                                    try {
                                        // Delete the old business case
                                        if (result.bc_id) {
                                            await permanentlyDeleteBusinessCase(result.bc_id.toString());
                                        }
                                        // Resume task
                                        backgroundTaskManager.resumeTask(taskId);
                                        resolve(result);
                                    } catch (error: any) {
                                        reject(error);
                                    }
                                },
                                onCancel: () => {
                                    backgroundTaskManager.cancelTask(taskId);
                                    reject(new Error('User cancelled operation'));
                                },
                            });
                        });
                    }
                    
                    return result;
                },
                timeout: 120000
            });
        }
        
        steps.push({
            name: 'Validating file',
            fn: async () => {
                const validation = await validateFile({
                    fileName: selectedFile.name,
                    fileId: selectedFile.id,
                    validationType: 'update',
                    bcId: businessCaseId
                });
                
                // Check for project name mismatch (backend returns error_type: 'project_mismatch')
                if (validation.error_type === 'project_mismatch' && !userProjectNameChoice) {
                    // Pause task and show global modal
                    backgroundTaskManager.pauseTask(taskId);
                    
                    return new Promise<IFileValidationResponse>((resolve, reject) => {
                        globalModalManager.showProjectNameMismatch({
                            originalProjectName: validation.original_project_name || 'Current',
                            fileProjectName: validation.file_project_name || 'New',
                            taskId,
                            onChooseOriginal: async () => {
                                // User chose to keep the original/current project name
                                userProjectNameChoice = 'original';
                                backgroundTaskManager.resumeTask(taskId, { projectNameChoice: userProjectNameChoice });
                                
                                // Return a modified validation that marks it as valid to continue
                                resolve({
                                    ...validation,
                                    is_valid: true,  // Allow continuation after user choice
                                    error_type: null,
                                    error_message: null
                                });
                            },
                            onChooseNew: async () => {
                                // User chose to use the new/file project name
                                userProjectNameChoice = 'new';
                                backgroundTaskManager.resumeTask(taskId, { projectNameChoice: userProjectNameChoice });
                                
                                // Return a modified validation that marks it as valid to continue
                                resolve({
                                    ...validation,
                                    is_valid: true,  // Allow continuation after user choice
                                    error_type: null,
                                    error_message: null
                                });
                            },
                            onCancel: () => {
                                backgroundTaskManager.cancelTask(taskId);
                                reject(new Error('User cancelled operation'));
                            },
                        });
                    });
                }
                
                // Handle other validation errors (duplicate CPR date, missing data, etc.)
                if (!validation.is_valid) {
                    throw new Error(validation.error_message || 'File validation failed');
                }
                
                return validation;
            },
            timeout: 120000
        });
    }

    steps.push({
        name: 'Updating business case',
        fn: async () => {
            return await updateBusinessCase(businessCaseId, selectedFile, rows, userProjectNameChoice);
        },
        timeout: 120000
    });

    if (onProgress) {
        backgroundTaskManager.subscribe(taskId, onProgress);
    }

    const result = await backgroundTaskManager.executeTask(
        'update',
        businessCaseName,
        steps,
        taskId
    );

    return { taskId, result: result.result };
};

export interface IFileValidationRequest {
    fileName: string;
    fileId?: string;
    validationType: 'create' | 'update' | 'same_file_check';
    bcId?: string;
}

export interface IFileValidationResponse {
    is_valid: boolean;
    error_type: string | null;
    error_message: string | null;
    bc_id?: number;
    project_name?: string;
    original_project_name?: string;  // Current project name in DB
    file_project_name?: string;      // New project name from file
    bc_name?: string;                // Existing BC name (bc_owner)
    gpm?: string;                    // Existing GPM
}

// Add this function
export const validateFile = async (request: IFileValidationRequest): Promise<IFileValidationResponse> => {
    try {
        const sessionId = generateSessionId();
        const response = await api.post('/business-cases/validate-file/', { ...request, sessionId }, { timeout: 120000 });
        return response.data;
    } catch (error: any) {
        console.error('Error validating file:', error);
        
        // If it's a validation error response (409, 422, etc.), return the validation data
        if (error?.response?.data && typeof error.response.data === 'object') {
            // Check if it's a validation response structure
            if ('is_valid' in error.response.data) {
                return error.response.data;
            }
        }
        
        // For other errors, create a generic validation failure response
        return {
            is_valid: false,
            error_type: 'validation_error',
            error_message: error?.response?.data?.error || error.message || 'File validation failed'
        };
    }
};

export const deleteBusinessCase = async (businessCaseId: string): Promise<{ message: string; deleted_at: string; deleted_by: string }> => {
    try {
        const response = await api.delete(`/business-cases/${businessCaseId}/delete/`);
        return response.data;
    } catch (error: any) {
        console.error('Error deleting business case:', error);
        
        // Handle specific error responses
        if (error?.response?.status === 404) {
            const errorMessage = error?.response?.data?.error || 'Business case not found or already deleted.';
            const customError = new Error(errorMessage);
            (customError as any).response = error.response;
            throw customError;
        }
        
        throw error;
    }
};

export const restoreBusinessCase = async (businessCaseId: string): Promise<{ message: string; restored_at: string }> => {
    try {
        const response = await api.post(`/business-cases/${businessCaseId}/restore/`);
        return response.data;
    } catch (error: any) {
        console.error('Error restoring business case:', error);
        throw error;
    }
};

export interface IDeletedProjectCheckResponse {
    has_deleted_project: boolean;
    project_name?: string;
    deleted_at?: string;
    deleted_by_name?: string;
    bc_id?: number;
}


export const checkDeletedProject = async (fileName: string): Promise<IDeletedProjectCheckResponse> => {
    try {
        const sessionId = generateSessionId();
        const response = await api.post('/business-cases/check-deleted-project/', { fileName, sessionId }, { timeout: 120000 });
        return response.data;
    } catch (error) {
        console.error('Error checking deleted project:', error);
        throw error;
    }
};

export const permanentlyDeleteBusinessCase = async (businessCaseId: string): Promise<{ message: string; project_name: string }> => {
    try {
        const response = await api.delete(`/business-cases/${businessCaseId}/permanent-delete/`);
        return response.data;
    } catch (error: any) {
        console.error('Error permanently deleting business case:', error);
        throw error;
    }
};

export interface IMaterialPaginatedResponse<T> {
  results: T[];
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface IMaterialDetail {
  material: string;
  product_family: string;
  product_group: string;
  product_subgroup: string;
}

export const getMaterialFamilies = async (
  search?: string,
  page: number = 1,
  pageSize: number = 100
): Promise<IMaterialPaginatedResponse<string>> => {
  try {
    const response = await api.get('/business-cases/materials/families/', {
      params: { search, page, page_size: pageSize }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching material families:', error);
    throw error;
  }
};

export const getMaterialGroups = async (
  families?: string[],
  search?: string,
  page: number = 1,
  pageSize: number = 100
): Promise<IMaterialPaginatedResponse<string>> => {
  try {
    const response = await api.get('/business-cases/materials/groups/', {
      params: {
        families: families?.join(','),
        search,
        page,
        page_size: pageSize
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching material groups:', error);
    throw error;
  }
};

export const getMaterialSubgroups = async (
  families?: string[],
  groups?: string[],
  search?: string,
  page: number = 1,
  pageSize: number = 100
): Promise<IMaterialPaginatedResponse<string>> => {
  try {
    const response = await api.get('/business-cases/materials/subgroups/', {
      params: {
        families: families?.join(','),
        groups: groups?.join(','),
        search,
        page,
        page_size: pageSize
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching material subgroups:', error);
    throw error;
  }
};

export const getMaterialList = async (
  families?: string[],
  groups?: string[],
  subgroups?: string[],
  search?: string,
  page: number = 1,
  pageSize: number = 100
): Promise<IMaterialPaginatedResponse<IMaterialDetail>> => {
  try {
    const response = await api.get('/business-cases/materials/list/', {
      params: {
        families: families?.join(','),
        groups: groups?.join(','),
        subgroups: subgroups?.join(','),
        search,
        page,
        page_size: pageSize
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching materials:', error);
    throw error;
  }
};

export const getMaterialDetails = async (
  materials: string[]
): Promise<Record<string, IMaterialDetail>> => {
  try {
    const response = await api.post('/business-cases/materials/details/', {
      materials
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching material details:', error);
    throw error;
  }
};

const isExportEnabled = (): boolean => {
    return import.meta.env.VITE_ENABLE_EXCEL_EXPORT === 'true';
};

export const exportBusinessCaseToExcel = async (businessCaseId: string): Promise<Blob> => {
    if (!isExportEnabled()) {
        throw new Error('Excel export feature is not available in this environment.');
    }

    try {
        const response = await api.get(`/business-cases/${businessCaseId}/export/`, {
            responseType: 'blob',
            timeout: 120000,
        });
        
        return response.data;
    } catch (error) {
        console.error(`Error exporting business case ${businessCaseId}:`, error);
        throw error;
    }
};

export interface IVersion {
    id: string;
    version: string;
    bc_file_name: string;
    last_updated_at: string;
    last_updated_by: string;
}

export interface IUpdateVersionResponse {
    message: string;
    version: IVersion;
}

export const updateVersionNumber = async (
    businessCaseId: string, 
    versionId: string, 
    newVersionNumber: string
): Promise<IUpdateVersionResponse> => {
    try {
        const response = await api.patch(
            `/business-cases/${businessCaseId}/versions/${versionId}/update/`,
            { version: newVersionNumber }
        );
        return response.data;
    } catch (error: any) {
        console.error(`Error updating version number:`, error);
        throw error;
    }
};

export interface IBatchVersionUpdate {
    id: string;
    version: string;
}

export interface IBatchUpdateVersionResponse {
    message: string;
    updated_versions: Array<{
        id: string;
        old_version: string;
        new_version: string;
        bc_file_name: string;
    }>;
}

export const batchUpdateVersionNumbers = async (
    businessCaseId: string,
    versions: IBatchVersionUpdate[]
): Promise<IBatchUpdateVersionResponse> => {
    try {
        const response = await api.patch(
            `/business-cases/${businessCaseId}/versions/batch-update/`,
            { versions }
        );
        return response.data;
    } catch (error: any) {
        console.error(`Error batch updating version numbers:`, error);
        throw error;
    }
};

export interface IVersionSnapshot {
    project_name: string;
    file_name: string;
    sheet_name: string;
    blob_url: string;
    modified_on: string | null;
    created_at: string | null;
}

export interface IVersionSnapshotsResponse {
    snapshots: IVersionSnapshot[];
    version_id: string;
    file_name: string;
    project_name: string;
}

export const getVersionSnapshots = async (
    businessCaseId: string,
    versionId: string
): Promise<IVersionSnapshotsResponse> => {
    try {
        const response = await api.get(
            `/business-cases/${businessCaseId}/versions/${versionId}/snapshots/`
        );
        return response.data;
    } catch (error: any) {
        console.error(`Error fetching version snapshots:`, error);
        throw error;
    }
};